import React, { useState } from 'react';
import {
    Box,
    Button,
    Link,
    TextField,
    Stack,
    Typography
} from '@mui/material';
import { NavLink } from "react-router-dom";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ConfirmDialog from './ConfirmDialog';


const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, ''); // Remove non-digit characters
    const phoneLength = phoneNumber.length;

    if (phoneLength < 4) {
        return phoneNumber;
    }
    else if (phoneLength < 7) {
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    else {
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
};

export default function ContactForm() {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [disabled, setDisabled] = useState(false);
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({
        fullname: '',
        phone: '',
        email: '',
        company: '',
        comments: '',
    });
    const [errors, setErrors] = useState({});

    const handleSubmit = async () => {
        if (!executeRecaptcha) return;
        
        const errors = {};

        if (!data.fullname) errors.fullname = 'Full name is required.';
        if (!data.phone)    errors.phone    = 'Phone number is required.';
        if (!data.email)    errors.email    = 'Email is required.';
        if (!data.company)  errors.company  = 'Company is required.';
        if (!data.comments) errors.comments = 'Comments are required.';

        // Set the error state
        setErrors(errors);

        // If there are any errors, stop the submission
        if (Object.keys(errors).length > 0) {
            return;
        }
        setDisabled(true);

        try {
            const token = await executeRecaptcha("submit_action");

            const response = await fetch(`${process.env.REACT_APP_API_URL}/public/lead`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({token, ...data})
            });
            if (response.ok) {
                setOpen(true);
            }
        }
        catch (error) {
            console.error(error);
        }
        setData(Object.fromEntries(Object.keys(data).map(key => [key, ''])));
        setDisabled(false);
    }

    const handleInputChange = ({ target: { name, value } }) => {
        const formattedValue = name === 'phone' ? formatPhoneNumber(value) : value;
        setData((prev) => ({ ...prev, [name]: formattedValue }));
        setErrors((prev) => ({ ...prev, [name]: null }));
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            bgcolor: "#f8f6f6",
            alignItems: { xs: 'stretch', sm: 'center' }
        }}>
            <Stack spacing={1} sx={{width: {xs: '100%', sm: '470px'}}}>
                <TextField
                    placeholder="Full Name"
                    name="fullname"
                    value={data.fullname}
                    type="text"
                    variant="outlined"
                    error={!!errors.fullname}
                    helperText={errors.fullname}
                    onChange={handleInputChange}/>
                <TextField
                    placeholder="Phone"
                    name="phone"
                    value={data.phone}
                    type="tel"
                    variant="outlined"
                    error={!!errors.phone}
                    helperText={errors.phone}
                    onChange={handleInputChange}/>
                <TextField 
                    placeholder="Email"
                    name="email"
                    value={data.email}
                    type="email"
                    variant="outlined"
                    error={!!errors.email}
                    helperText={errors.email}
                    onChange={handleInputChange}/>
                <TextField
                    placeholder="Company"
                    name="company"
                    value={data.company}
                    type="text"
                    variant="outlined"
                    error={!!errors.company}
                    helperText={errors.company}
                    onChange={handleInputChange}/>
                <TextField
                    placeholder="Comments"
                    multiline rows={4}
                    name='comments'
                    value={data.comments}
                    error={!!errors.comments}
                    helperText={errors.comments}
                    onChange={handleInputChange}/>
                <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    By providing your phone number and/or email address above and pressing [Submit] below, you consent and authorize
                    PR Innova Inc. and its representatives to contact you via phone call, text message (including by automated
                    means), and/or email for informational, marketing, and promotional purposes. Standard message and data 
                    rates may apply. You understand that your consent is not a condition of any purchase. You may opt out of
                    communications at any time by following the unsubscribe instructions in our messages or by contacting us
                    directly. For more information on how we handle your data, please review our{' '}
                    <Link component={NavLink} to="/privacy" underline='hover' color='text.primary'>
                        Privacy Policy
                    </Link >{' and '} 
                    <Link component={NavLink} to="/terms" underline='hover' color='text.primary'>
                        Terms of Use.
                    </Link >
                </Typography>
                <Button loading={disabled} type='submit' variant="contained" onClick={handleSubmit} sx={{bgcolor: '#003a66'}}>
                    Submit
                </Button>
            </Stack>
            {open && (<ConfirmDialog open={open} setOpen={setOpen}/>)}
        </Box>
    )
}
